import { onMounted, onUnmounted, type Ref } from 'vue'

import { prefersReducedMotion } from '@/utils/onEnter'

/** Токен подсветки: [тип, значение?]. Типы `nl`/`ind` — перевод строки и отступ. */
type CodeToken = readonly [type: string, value?: string]

/** Сниппет, печатаемый в hero-окне кода (engineer.ts). */
const CODE_TOKENS: readonly CodeToken[] = [
  ['kw', 'const'], ['pn', ' '], ['var', 'engineer'], ['pn', ' = '], ['pn', '{'], ['nl'],
  ['ind'], ['key', 'role'], ['pn', ': '], ['str', '"Frontend Architect"'], ['pn', ','], ['nl'],
  ['ind'], ['key', 'years'], ['pn', ': '], ['num', '8'], ['pn', ','], ['pn', '          '], ['com', '// since 2017'], ['nl'],
  ['ind'], ['key', 'leads'], ['pn', ': '], ['str', '"micro-frontends"'], ['pn', ','], ['nl'],
  ['ind'], ['key', 'stack'], ['pn', ': '], ['pn', '['], ['str', '"Vue"'], ['pn', ', '], ['str', '"Nuxt"'], ['pn', ', '], ['str', '"TS"'], ['pn', '],'], ['nl'],
  ['ind'], ['key', 'shipped'], ['pn', ': '], ['num', '50+'], ['pn', ','], ['pn', '        '], ['com', '// in prod'], ['nl'],
  ['ind'], ['key', 'status'], ['pn', ': '], ['str', '"open to lead roles"'], ['pn', ','], ['nl'],
  ['pn', '}'], ['pn', ' '], ['kw', 'as'], ['pn', ' '], ['kw', 'const'], ['pn', ';'],
]

function appendToken(target: HTMLElement, [type, value]: CodeToken): void {
  if (type === 'nl') {
    target.appendChild(document.createElement('br'))
    return
  }
  if (type === 'ind') {
    target.appendChild(document.createTextNode('  '))
    return
  }
  const span = document.createElement('span')
  span.className = `sx-${type}`
  span.textContent = value ?? ''
  target.appendChild(span)
}

/**
 * Печатает сниппет кода токен за токеном в указанный элемент `<code>`.
 * При reduced-motion или data-anim="off" рендерит мгновенно. Завершает мигающим курсором.
 *
 * @param codeEl - ref на элемент `<code class="cw-code">`
 */
export function useTypewriter(codeEl: Ref<HTMLElement | null>): void {
  let timer = 0

  onMounted(() => {
    const node = codeEl.value
    if (node === null) return

    const cursor = document.createElement('span')
    cursor.className = 'cw-cursor'

    const reduce = prefersReducedMotion() || document.documentElement.getAttribute('data-anim') === 'off'

    node.replaceChildren()
    if (reduce) {
      CODE_TOKENS.forEach((token) => appendToken(node, token))
      node.appendChild(cursor)
      return
    }

    let i = 0
    const step = (): void => {
      const token = CODE_TOKENS[i]
      if (token === undefined) {
        node.appendChild(cursor)
        return
      }
      appendToken(node, token)
      const [type, value] = token
      i++
      const delay = type === 'nl' ? 70 : type === 'ind' ? 10 : Math.min(85, 22 + (value ? value.length : 1) * 7)
      timer = window.setTimeout(step, delay)
    }
    timer = window.setTimeout(step, 420)
  })

  onUnmounted(() => clearTimeout(timer))
}
